import { DeliveryType } from "@omni/types/api/product/bff/shared/enums/deliveryType.enum";

import { IState } from "@/redux/store.types";
import {
    ExplodedCTA,
    explodeCTAProductList,
    getAction
} from "@/utils/callToActions/callToActions.utils";
import { CallToActionTypes } from "@/utils/enums";

import { ProductCallToAction } from "./@types/cart.types";
import { getCart } from "./cart.selectors";

const REMOVE_ARTICLE_TYPES = [
    CallToActionTypes.ACCEPT_NEGATIVE_DELIVERY_PROMISE,
    CallToActionTypes.CHANGE_QUANTITIES,
    CallToActionTypes.CHANGE_STORE,
    CallToActionTypes.CHANGE_STORE_AND_CNC,
    CallToActionTypes.CHOOSE_STORE,
    CallToActionTypes.CHANGE_TO_CNC,
    CallToActionTypes.CHANGE_TO_HOME_DELIVERY
];

const getProductsWithCallToActions = (
    state: IState
): Array<ProductCallToAction> => {
    const shoppingCart = getCart(state);

    if (!shoppingCart) {
        return [];
    }

    const allCartItems = [...shoppingCart.items];
    const collectProducts = allCartItems.filter(
        (p) => p.deliveryType === DeliveryType.ClickAndCollect
    );

    const pushProductCTA = (
        p: ExplodedCTA,
        allCartItems: Array<any>,
        acc: Array<ProductCallToAction>
    ) => {
        const product = acc.find((cta) => cta.product?.sku === p.sku);
        const {
            closable = false,
            action,
            visual,
            isExtended = false,
            isSilentAction
        } = getAction(p.type, !!p.sku);

        if (product) {
            product.actions.push({
                type: p.type,
                parameters: p.parameters,
                productParameters: p.productParameters,
                closable,
                action,
                isSilentAction,
                visual,
                isExtended
            });
        } else {
            acc.push({
                product: allCartItems.find((scp) => scp.sku === p.sku),
                actions: [
                    {
                        type: p.type,
                        parameters: p.parameters,
                        productParameters: p.productParameters,
                        closable,
                        action,
                        visual,
                        isSilentAction,
                        isExtended
                    }
                ]
            });
        }
    };

    const callToActions =
        shoppingCart?.callToActions.reduce<Array<ProductCallToAction>>(
            (acc, curr) => {
                if (curr.products.length > 0) {
                    const productList = explodeCTAProductList(curr);
                    productList.forEach((p) => {
                        pushProductCTA(p, allCartItems, acc);
                    });
                } else {
                    pushProductCTA(
                        {
                            type: curr.type,
                            parameters: curr.parameters || [],
                            sku: undefined,
                            productParameters: collectProducts.map((p) => p.sku)
                        },
                        allCartItems,
                        acc
                    );
                }
                return acc;
            },
            []
        ) || [];

    // For every product call to action:
    // - check if it needs an additional remove_article cta
    // - and that it doesn't already have one
    // - and it has a product
    // => include if both of the above are true
    callToActions.forEach((pCTA) => {
        if (
            pCTA.actions.some((a) =>
                REMOVE_ARTICLE_TYPES.some((t) => t === a.type)
            ) &&
            pCTA.actions.every(
                (a) => a.type !== CallToActionTypes.REMOVE_ARTICLE
            ) &&
            pCTA.product
        ) {
            const {
                closable = false,
                action,
                visual,
                isSilentAction,
                isExtended
            } = getAction(CallToActionTypes.REMOVE_ARTICLE, true);
            pCTA.actions.push({
                type: CallToActionTypes.REMOVE_ARTICLE,
                parameters: [pCTA.product?.sku],
                productParameters: [],
                closable,
                action,
                visual,
                isExtended,
                isSilentAction
            });
        }
    });

    return callToActions;
};

export { getProductsWithCallToActions };
